import React, { useState } from "react";

import { api } from "../lib/api";
import { Pipeline } from "../types/crm";

interface CreatePipelineModalProps {
  onClose: () => void;
  onCreated: (pipeline: Pipeline) => void;
}

/**
 * Modal "Criar novo pipeline" (spec §4.4): nome + lista dinâmica de fases
 * reordenáveis. Mínimo de 2 fases. Pipelines existentes nunca são apagados.
 */
export function CreatePipelineModal({ onClose, onCreated }: CreatePipelineModalProps) {
  const [name, setName] = useState("");
  const [stages, setStages] = useState<string[]>(["", ""]);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateStage = (index: number, value: string) => {
    setStages((current) => current.map((s, i) => (i === index ? value : s)));
  };

  const addStage = () => setStages((current) => [...current, ""]);

  const removeStage = (index: number) => {
    if (stages.length <= 2) {
      setError("Um pipeline precisa de pelo menos duas fases.");
      return;
    }
    setStages((current) => current.filter((_, i) => i !== index));
  };

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= stages.length) return;
    setStages((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Dê um nome ao funil.");
      return;
    }
    const filled = stages.map((s) => s.trim()).filter(Boolean);
    if (filled.length < 2) {
      setError("Preencha pelo menos duas fases.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await api.post<Pipeline>("/pipelines/", {
        name: name.trim(),
        stages: filled,
      });
      onCreated(response.data);
    } catch {
      setError("Não foi possível criar o pipeline. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/30 py-16">
      <div className="w-full max-w-[460px] rounded-xl border border-border-subtle bg-white p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">Criar novo pipeline</h2>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            ✕
          </button>
        </div>

        <form className="mt-4 space-y-4" onSubmit={handleSubmit}>
          <div>
            <label htmlFor="cp_name" className="block text-sm font-medium text-slate-700">
              Nome do funil
            </label>
            <input
              id="cp_name"
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Ex: Expansão de contas"
              className="mt-1 block w-full rounded-[10px] border border-border-subtle bg-surface px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700">Fases</label>
            <div className="mt-1 space-y-2">
              {stages.map((stage, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="cursor-default select-none text-slate-300">⠿</span>
                  <input
                    type="text"
                    value={stage}
                    onChange={(event) => updateStage(index, event.target.value)}
                    placeholder={`Fase ${index + 1}`}
                    className="flex-1 rounded-[10px] border border-border-subtle bg-surface px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                  <button
                    type="button"
                    onClick={() => move(index, -1)}
                    disabled={index === 0}
                    className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    aria-label="Mover para cima"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => move(index, 1)}
                    disabled={index === stages.length - 1}
                    className="px-1 text-slate-400 hover:text-slate-700 disabled:opacity-30"
                    aria-label="Mover para baixo"
                  >
                    ▼
                  </button>
                  <button
                    type="button"
                    onClick={() => removeStage(index)}
                    className="px-1 text-slate-400 hover:text-red-600"
                    aria-label="Remover fase"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addStage}
              className="mt-2 w-full rounded-[10px] border border-dashed border-border-subtle py-2 text-sm font-medium text-slate-500 hover:bg-surface"
            >
              ＋ Adicionar fase
            </button>
          </div>

          {error && (
            <div className="rounded-[10px] bg-red-50 px-3 py-2 text-sm text-red-600">{error}</div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-[10px] px-4 py-2 text-sm font-medium text-slate-600 hover:bg-surface"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-[10px] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark disabled:opacity-60"
            >
              {isSubmitting ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
